import i18n from '@/i18n'
import {profileService} from '@/services/profile.service'
import {mapActions} from 'vuex'
import {SnackbarStyle} from '@/constants/constants'
import {DialogUserProfile} from "@/tools/dialog"
import Vue from 'vue'
import {clone} from 'ramda'
import {EventHub} from '@/constants/constants'
import AutocompleteComponent from "@/components/commons/autocomplete/AutocompleteComponent.vue";

export default {
    name: 'ProfileDialogComponent',
    components: {
        AutocompleteComponent
    },
    model: {
        prop: 'model',
        event: 'change'
    },
    props: {
        model: {
            type: Object,
            required: true
        }
    },
    data: () => ({
        // model para el dialog
        showEditDialog: false,
        // título del dialog
        editDialogTitle: i18n.t('userProfiles.editDialog.titleNew'),
        // reglas de validación
        validationRules: {
            required: (value) => !!value || `${i18n.t('required')}`,
        },
        // mensajes de error para el nombre
        nameError: [],
        // model para el formulario
        validForm: false,
        // para validación del nombre
        actualName: null,
        // search del treeview
        search: null,
        // expandir / colapsar el treeview
        openAll: false,
        // esquema del perfil
        profileSchema: [],
        // model del perfil
        profileModel: {
            name: undefined, // nombre
            guest_user_profile: false, // perfil usuario invitado
            modules: [], // módulos del perfil
            enterpriseId: null
        }
    }),
    computed: {
        /**
         * Para validar que los datos de los modulos del perfil sean correctos.
         * Se usa para deshabilitar el botón save
         */
        validateModules() {
            return !this.profileModel.modules.length;
        }
    },
    created() {
    },
    async mounted() {
        await this.initialize();
    },
    methods: {
        /**
         * actions del snackbar
         */
        ...mapActions('snackbar', [
            'showSnackbar',
            'closeSnackbar'
        ]),
        /**
         * actions del dialog
         */
        ...mapActions('dialog', [
            'openDialog',
            'closeDialog'
        ]),
        /**
         * actions del breadcrumb
         */
        ...mapActions('breadcrumbs', [
            'pullBread',
            'pushBread'
        ]),
        /**
         * Obtiene los datos necesarios para el dialog
         */
        async initialize() {
            this.profileSchema = await profileService.getProfileSchema();
            let modules = clone(this.model.modules);
            const parents = [];
            this.setParents(parents, this.profileSchema);
            modules = this.removeParents(parents, modules);
            this.profileModel.modules = modules;
            Vue.nextTick(() => this.expandCollapse(true));
        },
        /**
         * Resetea los campos del formulario de creación/actualización
         */
        resetFields() {
            // model
            this.model.name = null;
            this.model.guest_user_profile = false;
            this.model.modules = [];
            this.model.enterpriseId = null;
            this.model.enterprises = [];
            this.model.update = false;
            // model que se edita en el formulario del componente
            this.profileModel = {
                name: null,
                guest_user_profile: false,
                modules: [],
                enterpriseId: null
            };
            if (this.$refs.profileForm) {
                this.$refs.profileForm.resetValidation()
            }
        },
        /**
         * Clic fuera del dialog
         */
        clickOutside() {
            this.cancel();
        },
        /**
         * Cancelar
         */
        cancel() {
            this.resetFields();
            this.pullBread(DialogUserProfile);
        },

        findParents(elementSet, children) {
            let hasSelected = false;
            children.forEach(children => {
                if (this.profileModel.modules.includes(children.key)) {
                    hasSelected = true;
                }
                if (children.children && this.findParents(elementSet, children.children)) {
                    hasSelected = true;
                    elementSet.add(children.key);
                }
            });
            return hasSelected;
        },

        setParents(parents, elements) {
            elements.forEach(element => {
                if (element.children) {
                    parents.push(element.key)
                    this.setParents(parents, element.children)
                }
            });
        },

        removeParents(parents, modules) {
            parents.forEach(parent => {
                const parentIndex = modules.indexOf(parent);
                if (parentIndex > -1) {
                    modules.splice(parentIndex, 1);
                }
            });
            return modules;
        },

        /**
         * Guardar
         */
        async save() {
            const profileDto = this.getProfileDto(this.model.update)
            if (this.model.update) {
                profileService.update(profileDto).then(response => {
                    if (response && response.success) {
                        this.showMessage(this.$t(response.message), SnackbarStyle.SUCCESS, 10000);
                        this.resetFields();
                        this.$emit('commitProfile');
                        this.$eventBus.$emit(EventHub.COMMIT_USER_PROFILE, profileDto);
                        this.cancel();
                    }
                })
            } else {
                profileService.create(profileDto).then(response => {
                    if (response && response.success) {
                        this.showMessage(this.$t(response.message), SnackbarStyle.SUCCESS, 10000);
                        this.resetFields();
                        this.$emit('commitProfile');
                        profileDto._id = response.id;
                        this.$eventBus.$emit(EventHub.COMMIT_USER_PROFILE, profileDto);
                        this.cancel();
                    }
                })
            }
        },
        /**
         * Genera el DTO para actualizar / crear un perfil
         * @param {*} update
         */
        getProfileDto(update = false) {
            const elementSet = new Set();
            this.findParents(elementSet, this.profileSchema)
            const parents = Array.from(elementSet);
            const allElements = this.profileModel.modules.concat(parents);
            const result = {
                name: this.profileModel.name,
                modules: allElements,
                guest_user_profile: this.profileModel.guest_user_profile,
                enterpriseId: this.profileModel.enterpriseId._id
            }
            if (update) {
                result.id = this.model._id
            }
            return result
        },
        /**
         * Método para validar el nombre en el evento onBlur
         */
        nameDuplicated() {
            if (this.actualName === this.profileModel.name) {
                return;
            }
            profileService.validateName(this.profileModel.name).then(response => {
                if (response) {
                    this.nameError.push(`${i18n.t('userProfiles.duplicate')}`);
                } else {
                    this.nameError = [];
                }
            })
        },
        /**
         * Muestra un snackbar con la config pasada en los params
         * @param {*} message
         * @param {*} style
         * @param {*} timeout
         */
        showMessage(message, style, timeout) {
            this.showSnackbar({visible: true, text: message, timeout: timeout, style: style});
        },
        /**
         * Expande / colapsa el árbol
         * @param {*} value
         */
        expandCollapse(value) {
            this.$refs.profileDialogTreeview.updateAll(value);
        },
        /**
         * Limpia los valores seleccionados
         */
        clearTree() {
            this.profileModel.modules = [];
        },
        /**
         * Selecciona todos los nodos del árbol
         */
        selectAll() {
            const result = [];
            profileService.selectAll(this.profileSchema, result);
            this.profileModel.modules = result;
        },
        /**
         * Cada vez que se modifica la selección de la empresa, se cargan
         */
        enterpriseSelectedFunction(newValue) {
            this.profileModel.enterpriseId = newValue.id ? newValue.id : newValue;
        },
    },
    watch: {
        search(newVal) {
            if (newVal) {
                this.$refs.profileDialogTreeview.updateAll(true);
            }
        },
        model: {
            deep: true,
            immediate: true,
            handler: function (newModel) {
                if (newModel) {
                    this.actualName = newModel.name;
                    this.profileModel.name = newModel.name;
                    this.profileModel.guest_user_profile = newModel.guest_user_profile;
                    this.profileModel.modules = clone(newModel.modules);
                    this.profileModel.enterpriseId = newModel.enterpriseId;
                    this.profileModel.enterprises = newModel.enterprises;
                }
            }
        }
    }
}
